import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWhatsAppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/whatsapp-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      setIsActive(data.is_active || false);
      setPhoneNumber(data.phone_number || "");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("admin_token");

    if (isActive && !phoneNumber.trim()) {
      toast.error("Telefon numarası gerekli");
      return;
    }

    // Telefon numarası validasyonu
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    if (isActive && cleanNumber.length < 10) {
      toast.error("Geçerli bir telefon numarası girin (örn: 905XXXXXXXXX)");
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/whatsapp-settings`,
        {
          is_active: isActive,
          phone_number: cleanNumber,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("WhatsApp ayarları güncellendi!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-teal-400">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-settings">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black text-gradient">WhatsApp Ayarları</h2>
        <div className="flex items-center gap-3">
          <Label className="text-teal-400 font-semibold">WhatsApp Botu Aktif</Label>
          <Switch
            checked={isActive}
            onCheckedChange={setIsActive}
            className="data-[state=checked]:bg-teal-500"
            data-testid="whatsapp-active-switch"
          />
        </div>
      </div>

      <div className="glass-card-premium rounded-2xl p-8 neon-border space-y-6">
        {/* Info */}
        <div className="bg-green-500/10 border-2 border-green-500/30 rounded-2xl p-5">
          <p className="text-green-400 text-sm font-semibold mb-2">
            <MessageCircle className="w-4 h-4 inline mr-2" />
            WhatsApp Floating Bot
          </p>
          <p className="text-gray-300 text-xs leading-relaxed">
            Aktif olduğunda, tüm sayfalarda sağ alt köşede WhatsApp destek butonu görünür.<br />
            Ziyaretçiler size doğrudan WhatsApp üzerinden ulaşabilir.
          </p>
        </div>

        {/* Phone Number */}
        <div className="space-y-3">
          <Label className="text-sm font-bold text-teal-400 uppercase tracking-wider">
            <Phone className="w-4 h-4 inline mr-2" />
            WhatsApp Telefon Numarası *
          </Label>
          <Input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="905XXXXXXXXX (ülke kodu ile)"
            className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg font-mono"
            data-testid="whatsapp-phone-input"
          />
          <p className="text-xs text-gray-500">
            ⚠️ Ülke kodu ile birlikte girin (örn: 905551234567)
          </p>
        </div>

        {/* Preview */}
        {isActive && phoneNumber && (
          <div className="bg-green-500/10 border-2 border-green-500/30 rounded-2xl p-6">
            <p className="text-xs text-green-400 font-bold uppercase mb-3">Önizleme</p>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold mb-1">Alcodot Destek</p>
                <p className="text-green-400 text-xs mb-2">● Online</p>
                <p className="text-gray-300 text-sm mb-3">
                  Merhaba! 👋<br />
                  Size nasıl yardımcı olabiliriz?
                </p>
                <div className="bg-green-600 text-white text-xs font-bold py-2 px-3 rounded-lg inline-block">
                  WhatsApp'tan Yazın →
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black text-lg shadow-lg neon-glow"
          data-testid="save-whatsapp-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? "Kaydediliyor..." : "WhatsApp Ayarlarını Kaydet"}
        </Button>
      </div>
    </div>
  );
};

export default AdminWhatsAppSettings;
